import * as React from 'react';

import useSubfolderLanguageAndLocale from 'bundles/internationalization-lib/hooks/useSubfolderLanguageAndLocale';
import { ELIGIBLE_DOMAINS_FOR_ARTICLES_RECS } from 'bundles/unified-description-page-common/constants';
import type { ArticleHit } from 'bundles/unified-description-page-common/contexts/consumer/ArticlesRecommendationsContext';
import ArticlesRecommendationsContext from 'bundles/unified-description-page-common/contexts/consumer/ArticlesRecommendationsContext';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import getArticleRecsSearchParameters from 'bundles/unified-description-page-common/utils/getArticleRecsSearchParameters';
import type { SearchParameters } from 'bundles/unified-seo-content-common/hooks/useAlgoliaQueryCSR';
import useAlgoliaQueryDataCSR from 'bundles/unified-seo-content-common/hooks/useAlgoliaQueryCSR';

const ARTICLES_INDEX_NAME = 'test_articles';

type PropsToDataFetcher = {
  searchParameters: SearchParameters;
  children: React.ReactNode;
};

const ArticlesRecommendationsDataProvider = ({ searchParameters, children }: PropsToDataFetcher) => {
  const { searching, results } = useAlgoliaQueryDataCSR<ArticleHit>(ARTICLES_INDEX_NAME, searchParameters);

  return (
    <ArticlesRecommendationsContext.Provider
      value={{
        recommendations: results,
        isArticlesRecsLoading: searching,
      }}
    >
      {children}
    </ArticlesRecommendationsContext.Provider>
  );
};

export const CourseArticlesRecsDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useCoursePageData();
  // We currently do not support Articles recommendations for non English Articles so we are disabling Articles recommendations on non English XDP pages
  const shouldNotShowArticleRecsOnNonEnglishXDPs = !!useSubfolderLanguageAndLocale();

  if (!pageData || shouldNotShowArticleRecsOnNonEnglishXDPs) {
    return <>{children}</>;
  }

  const domainId = pageData.domains?.[0]?.domainId;
  const searchParameters = getArticleRecsSearchParameters(pageData);

  if (domainId && ELIGIBLE_DOMAINS_FOR_ARTICLES_RECS.includes(domainId)) {
    return (
      <ArticlesRecommendationsDataProvider searchParameters={searchParameters}>
        {children}
      </ArticlesRecommendationsDataProvider>
    );
  }

  return <>{children}</>;
};

export const SpecializationArticlesRecsDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useSpecializationPageData();
  // We currently do not support Articles recommendations for non English Articles so we are disabling Articles recommendations on non English XDP pages
  const shouldNotShowArticleRecsOnNonEnglishXDPs = !!useSubfolderLanguageAndLocale();

  if (!pageData || shouldNotShowArticleRecsOnNonEnglishXDPs) {
    return <>{children}</>;
  }

  const domainId = pageData.domains?.[0]?.domainId;
  const searchParameters = getArticleRecsSearchParameters(pageData);

  if (domainId && ELIGIBLE_DOMAINS_FOR_ARTICLES_RECS.includes(domainId)) {
    return (
      <ArticlesRecommendationsDataProvider searchParameters={searchParameters}>
        {children}
      </ArticlesRecommendationsDataProvider>
    );
  }

  return <>{children}</>;
};
